import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  CheckCircleFilled,
  CloseCircleFilled,
  CloseOutlined,
  ExclamationCircleFilled,
  InfoCircleFilled,
} from '@ant-design/icons';
import styles from './Toast.module.css';

const ICONS = {
  success: CheckCircleFilled,
  error: CloseCircleFilled,
  warning: ExclamationCircleFilled,
  info: InfoCircleFilled,
};

const Toast = ({
  id,
  type = 'info',
  title,
  message,
  duration = 5000,
  onClose,
  action,
  secondaryAction,
  jobId,
  onActionClick
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [progress, setProgress] = useState(100);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(() => {
      onClose(id);
    }, 300); // Allow animation to complete
  }, [id, onClose]);

  useEffect(() => {
    if (duration > 0) {
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev <= 0) {
            handleClose();
            return 0;
          }
          return prev - (100 / (duration / 100));
        });
      }, 100);

      return () => clearInterval(interval);
    }
  }, [duration, handleClose]);

  const Icon = ICONS[type] || InfoCircleFilled;

  return (
    // The base .toast slides in; .hidden slides it out before it is removed. The card
    // is not a live region: ToastContainer announces it (see its live regions).
    <div className={`${styles.toast} ${styles[type]}${isVisible ? '' : ` ${styles.hidden}`}`}>
      <div className={styles.content}>
        <div className={styles.icon}>
          <Icon aria-hidden="true" />
        </div>
        <div className={styles.text}>
          {title && <div className={styles.title}>{title}</div>}
          <div className={styles.message}>{message}</div>
          {jobId && (
            <div className={styles.jobId}>
              Job ID: <code>{jobId}</code>
            </div>
          )}
        </div>
        <button
          className={styles.closeButton}
          onClick={handleClose}
          aria-label="Close notification"
        >
          <CloseOutlined aria-hidden="true" />
        </button>
      </div>

      {action && (
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => {
              // The action's own handler first (it used to be skipped, so
              // "View Results" and a confirm's button only closed the toast);
              // the toast closes even if that handler throws.
              try {
                if (action.onClick) action.onClick();
                if (onActionClick) onActionClick();
              } finally {
                handleClose();
              }
            }}
          >
            {action.label}
          </button>
          {secondaryAction && (
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={() => {
                if (secondaryAction.onClick) secondaryAction.onClick();
                handleClose();
              }}
            >
              {secondaryAction.label}
            </button>
          )}
        </div>
      )}

      {duration > 0 && (
        <div className={styles.progressBar}>
          <div
            className={styles.progress}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
};

Toast.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  duration: PropTypes.number,
  onClose: PropTypes.func.isRequired,
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func
  }),
  // A quieter second button beside the action (a confirm's Cancel).
  secondaryAction: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func
  }),
  jobId: PropTypes.string,
  onActionClick: PropTypes.func
};

export default Toast;
